/**
 * @file Browser and platform sniffing.
 * Device.ts will eventually be replaced by this.
 * Device does a bit too much (modifying HTML etc).
 */
import window from 'bits/global'

type BrowserOs = 'windows' | 'iphone' | 'ipad' | 'macos' | 'ios' | 'unknown' | 'android'

const navigator = window.navigator
const userAgent = navigator.userAgent.toLowerCase()
const screen = {
  width: window.screen.width,
  height: window.screen.height,
}
const windowHeight = window.innerHeight
const windowWidth = window.innerWidth

// Helper function to check if user agent includes a string.
// We can use String.prototype.includes here because polyfill adds it.
// However, i'd like this bit to be an independent module, capable of
// running on old devices as well.
const uaIncludes = (str: string): boolean => {
  return userAgent.includes(str)
}

const isTests: { [key: string]: () => boolean } = {}
const memoizedIsTests: { [key: string]: boolean } = {}

const canTests: { [key: string]: () => boolean } = {}
const memoizedCanTests: { [key: string]: boolean } = {}

// Check if Internet Explorer
isTests.ie = (): boolean => {
  return uaIncludes('msie') || (navigator.appName === 'Netscape' && uaIncludes('trident'))
}

isTests.iphone = (): boolean => {
  return uaIncludes('iphone')
}

isTests.ipadmobile = (): boolean => {
  return uaIncludes('ipad')
}

// Check if iPad in desktop mode. New iPadOS has this on by default
isTests.ipadmac = (): boolean => {
  return uaIncludes('macintosh') && 'ontouchend' in document
}

// Check if iPad
isTests.ipad = (): boolean => {
  return isTests.ipadmobile() || isTests.ipadmac()
}

isTests.ipod = (): boolean => {
  return uaIncludes('ipod')
}

isTests.android = (): boolean => {
  return uaIncludes('android')
}

isTests.ios = (): boolean => {
  return isTests.iphone() || isTests.ipad() || isTests.ipod()
}

declare global {
  interface Window {
    chrome?: {
      app?: object
    }
  }
}
// Check for supported browsers including Chrome, Edge, Opera, Brave etc
isTests.chromiumBased = (): boolean => {
  return !!window?.chrome?.app
}

// Check if cookies are enabled
canTests.cookies = (): boolean => {
  return navigator.cookieEnabled
}

// Safari will say cookies are enabled even when they are not.
// If we are sure that our page will have cookies, this extra check
// ensures that the browser is not lying about its ability to set cookies.
canTests.cookiesForReal = (): boolean => {
  return navigator.cookieEnabled && document.cookie !== ''
}

// Check if browser supports lazy loading natively.
canTests.lazyLoad = (): boolean => {
  return 'loading' in HTMLImageElement.prototype
}

// Check if browser supports WebAssembly
canTests.webAssembly = (): boolean => {
  // hat tip to https://stackoverflow.com/a/47880734/669
  return typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function'
}

function is(query: string): boolean {
  if (typeof memoizedIsTests[query] === 'undefined') {
    if (typeof isTests[query] !== 'undefined') {
      memoizedIsTests[query] = isTests[query]()
    } else {
      throw new Error(`Unsupported query ${query}`)
    }
  }
  return memoizedIsTests[query]
}

function can(query: string): boolean {
  if (typeof memoizedCanTests[query] === 'undefined') {
    if (typeof canTests[query] !== 'undefined') {
      memoizedCanTests[query] = canTests[query]()
    } else {
      throw new Error(`Unsupported query ${query}`)
    }
  }
  return memoizedCanTests[query]
}

function isTouchScreen(): boolean {
  if (window.matchMedia('(hover: none)').matches) {
    return true
  } else {
    return false
  }
}
function isDarkModeEnabled(): boolean {
  if (window?.matchMedia('(prefers-color-scheme: dark)').matches) {
    return true
  } else {
    return false
  }
}

const os = (): BrowserOs => {
  if (is('windows')) return 'windows'
  if (is('android')) return 'android'
  if (is('ios')) return 'ios'
  if (is('macos')) return 'macos'
  return 'unknown'
}

export {
  is,
  can,
  os,
  is as browserIs,
  can as browserCan,
  userAgent,
  screen,
  isTouchScreen,
  isDarkModeEnabled,
  windowHeight,
  windowWidth,
  BrowserOs,
}
